import { Divider, Stack, Typography } from "@mui/material"
import { DialogBox } from "lib/@components/dialogBox"
import { Done } from "lib/@components/done"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Bound } from "lib/@components/binding/Bound"
import { frag } from "lib/@components/slot/frag"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { AmendsEditAction } from "../amends/components/audience-aware-amends-editor"
import { AuthoringTailoredValue } from "../amends/components/authoring-tailored-value"
import { AuthoringAmendsDecisionBanner } from "./authoring-amends-decision-banner"
import { AmendsBoundary } from "../amends/components/amends-boundary"
import { AmendsBoundaryEditor } from "../amends/components/amends-boundary-editor"
import { createDraftAmendHandlers } from "../amends/controller/draft-amend-handlers"
import { RichTextComparison } from "./rich-text-comparison"
import { LifecycleFieldChip } from "./lifecycle-field-chip"
import { AmendsChangeType } from "authoring/lib/enum/amends-change-type.mjs"
import { getFrequencyLabel } from "library/get-frequency-label.mjs"
import { TaskComparisonRestoreActions } from "./task-comparison-restore-actions"
import { useSkills } from "routes/@lib/skills"

const RICH_TEXT_KEYS = new Set(["content", "note"])

export function TaskComparisonDialog({
    entry,
    original,
    tailored,
    auditKeys,
    taskTitle,
    draftTask,
    projectId,
    scheduleId,
    canRestore,
    editMode = true,
    macroMap,
}) {
    const [skillIndex] = useSkills()
    // Author notes are editable only for deleted tasks; otherwise remove the plugged Edit button
    AmendsEditAction.usePlug(editMode ? null : replaceAllWith(frag), [editMode])
    const isLifecycleChange = isTaskLifecycleEntry(entry)
    const isRemovedTask = isTaskRemovedEntry(entry)
    const showRestore = canRestore && isRemovedTask
    const { fieldKeys, richTextKeys, displayOriginal, displayTailored } = resolveDisplayFields({
        auditKeys,
        original,
        tailored,
        draftTask,
        isLifecycleChange,
        skillIndex,
    })
    const amendsKey = getAmendsKeyFromEntry(entry)
    const noteAmends = getNoteAmends(entry, amendsKey)
    const { saveAmend, deleteAmend } = createDraftAmendHandlers({ projectId, scheduleId })
    const decision = entry?.decision ?? null
    const hasDecision = Boolean(decision?.payload?.status)
    const ActionsComponent = showRestore ? TaskComparisonRestoreActions : Done
    const isHeader = Boolean(draftTask?.isHeader)

    return (
        <DialogBox title={taskTitle ?? "Task Changes"} Actions={ActionsComponent} isHeader={isHeader}>
            <Bound compareFormatting>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <If truthy={hasDecision}>
                        <AuthoringAmendsDecisionBanner decision={decision} expanded />
                    </If>

                    <If truthy={fieldKeys.length > 0}>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                            <If value={isLifecycleChange}>
                                <Repeat list={fieldKeys} item={<LifecycleFieldChip tailored={displayTailored} />} />
                            </If>
                            <If value={!isLifecycleChange}>
                                <Repeat
                                    list={fieldKeys}
                                    item={
                                        <AuthoringTailoredValue original={displayOriginal} tailored={displayTailored} />
                                    }
                                />
                            </If>
                        </Stack>
                    </If>

                    <If truthy={richTextKeys.includes("content")}>
                        <RichTextComparison
                            label="Task Content"
                            originalValue={displayOriginal?.content}
                            tailoredValue={displayTailored?.content}
                            isNew={isLifecycleChange}
                        />
                    </If>

                    <If truthy={richTextKeys.includes("note")}>
                        <RichTextComparison
                            label="Task Notes"
                            originalValue={displayOriginal?.note}
                            tailoredValue={displayTailored?.note}
                            isNew={isLifecycleChange}
                        />
                    </If>

                    <If truthy={Boolean(amendsKey)}>
                        <Divider />
                        <Typography variant="subtitle2">Author Notes</Typography>
                        <Bound macroMap={macroMap}>
                            <AmendsBoundary
                                amends={normalizeAmendsForBoundary(noteAmends, amendsKey)}
                                onSaveAmend={saveAmend}
                                onDeleteAmend={deleteAmend}
                                options={{ editMode }}
                            >
                                <AmendsBoundaryEditor amendsKey={amendsKey} alwaysOpen />
                            </AmendsBoundary>
                        </Bound>
                    </If>
                </Stack>
            </Bound>
        </DialogBox>
    )
}

function getAmendsKeyFromEntry(entry) {
    const entryKey = entry?.key ?? ""
    if (!entryKey.startsWith("task:")) return null
    const taskId = entryKey.slice("task:".length)
    return taskId ? `tasks.${taskId}` : null
}

function getNoteAmends(entry, amendsKey) {
    if (!amendsKey || !entry?.amends) return []
    const matching = entry.amends.filter((amend) => {
        const key = amend?.payload?.amendsKey ?? amend?.payload?.field
        return key === amendsKey
    })
    if (matching.length <= 1) return matching
    // AmendsBoundary throws on duplicate amendsKeys — keep only the most recent record
    const sorted = [...matching].sort((a, b) => new Date(b?.date ?? 0) - new Date(a?.date ?? 0))
    return [sorted[0]]
}

function normalizeAmendsForBoundary(amends, amendsKey) {
    return amends.map((amend) => {
        if (amend?.payload?.amendsKey) return amend
        return {
            ...amend,
            payload: { ...amend.payload, amendsKey },
        }
    })
}

function isTaskLifecycleEntry(entry) {
    const payload = entry?.primaryAmend?.payload ?? {}
    return payload?.changeType === AmendsChangeType.ADDED || payload?.changeType === AmendsChangeType.REMOVED
}

function isTaskRemovedEntry(entry) {
    const payload = entry?.primaryAmend?.payload ?? {}
    return payload?.changeType === AmendsChangeType.REMOVED
}

function resolveDisplayFields({ auditKeys, original, tailored, draftTask, isLifecycleChange, skillIndex }) {
    if (!draftTask) {
        const allKeys = auditKeys ?? []
        return {
            fieldKeys: allKeys.filter((key) => key !== "unitOfMeasure" && !RICH_TEXT_KEYS.has(key)),
            richTextKeys: allKeys.filter((key) => RICH_TEXT_KEYS.has(key)),
            displayOriginal: original,
            displayTailored: tailored,
        }
    }

    const taskTailored = buildTaskAsTailored(draftTask, skillIndex)
    const taskOriginal = isLifecycleChange ? buildEmptyOriginal(taskTailored) : { ...taskTailored }
    const allFieldKeys = Object.keys(taskTailored).filter((key) => taskTailored[key] != null)
    const changeTypeKey = (auditKeys ?? []).includes("changeType") ? ["changeType"] : []

    return {
        fieldKeys: [
            ...changeTypeKey,
            ...allFieldKeys.filter((key) => key !== "unitOfMeasure" && !RICH_TEXT_KEYS.has(key)),
        ],
        richTextKeys: allFieldKeys.filter((key) => RICH_TEXT_KEYS.has(key)),
        displayOriginal: { ...taskOriginal, ...(original ?? {}) },
        displayTailored: { ...taskTailored, changeType: tailored?.changeType, ...(tailored ?? {}) },
    }
}

function buildTaskAsTailored(task, skillIndex) {
    const result = {}

    if (task.classification) result.classification = task.classification
    if (task.minutes != null) result.minutes = task.minutes
    if (task.frequency) {
        result.frequency = toFrequencyDisplay(task.frequency)
    }
    if (task.skillSet) {
        result.skill = skillIndex[task.skillSet]
    }
    if (task.skill) result.skill = task.skill
    if (task.title) result.title = task.title
    if (hasVisibleHtmlContent(task.content)) result.content = task.content
    if (hasVisibleHtmlContent(task.note)) result.note = task.note

    return result
}

function toFrequencyDisplay(frequency) {
    if (!frequency) return null
    if (typeof frequency === "string") return { label: frequency }
    if (typeof frequency === "object" && "label" in frequency) return frequency
    if (typeof frequency === "object" && "period" in frequency) {
        return { label: getFrequencyLabel(frequency) }
    }
    return { label: String(frequency) }
}

function buildEmptyOriginal(tailored) {
    const result = {}
    for (const key of Object.keys(tailored)) {
        if (key === "classification") result.classification = ""
        else if (key === "minutes") result.minutes = 0
        else if (key === "frequency") result.frequency = { label: "" }
        else if (key === "skill") result.skill = { Skilling: "", SkillingCode: "" }
        else result[key] = ""
    }
    return result
}

function hasVisibleHtmlContent(value) {
    return `${value ?? ""}`.replace(/<[^>]*>/g, "").trim().length > 0
}
